import type { PlanAssumptions } from 'helpers/plan-assumptions';
import type { PlanEvaluation } from '@/state/experiments/useExperimentPlan';
import type { useExperimentPlan } from '@/state/experiments/useExperimentPlan';
import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MIN_PLAN_DAYS } from 'helpers/experiment-plan';
import { cn } from 'helpers/cn';
import { formatRunLength, pct } from './plan-format';

const SECONDARY_BUTTON_CLASS =
  'border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta';
const PRIMARY_BUTTON_CLASS = 'bg-cta hover:bg-ctahover h-8 rounded-lg px-3 text-meta text-white';

type Step = 'lift' | 'traffic' | 'rate' | 'summary';
// The facts first, the choice last: traffic and rate are about the site, the lift is a
// judgement. Asked in this order, the run lengths shown on the lift step are built from
// the answers just given rather than from defaults the next two steps would change.
const STEPS: readonly Step[] = ['traffic', 'rate', 'lift', 'summary'];

/**
 * Presets rather than a bare number field: "what lift should I plan for" is the one
 * question people cannot answer from nothing, and a description of what each size
 * usually means is most of the answer.
 */
const LIFT_PRESETS = [
  { value: 2, label: 'Small refinement', hint: 'Copy or styling tweaks on a high-traffic page' },
  { value: 5, label: 'Typical change', hint: 'A clear change to one element or message' },
  { value: 10, label: 'Visible change', hint: 'A new layout for a section, or a new offer' },
  { value: 20, label: 'Bold redesign', hint: 'A different page, flow or proposition' },
] as const;

/**
 * How much of the site a test typically reaches. Offered as picks rather than a field:
 * the honest answer is rarely more precise than "about half", and a test on one landing
 * page and a sitewide header test differ by an order of magnitude, not by a few percent.
 */
const SITE_SHARES = [100, 50, 25, 10] as const;

type TrafficMode = 'history' | 'site' | 'own';

type PlanWizardProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  manager: ReturnType<typeof useExperimentPlan>;
};

/** Text for a number field: empty for "not set", so the placeholder shows through. */
const asText = (value: number | null) => (value === null ? '' : String(value));

/**
 * Parsed on read, not in the setter, so a field keeps exactly what was typed: parsing
 * in the setter turned the "0" of "0.5" into nothing and made rates under 1% untypeable.
 */
const parsePositive = (text: string): number | null => {
  if (text.trim() === '') return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Plan how long an experiment must run, one question at a time: the traffic entering
 * the test, the original's conversion rate, then the lift worth detecting.
 *
 * The lift step shows each option's run length, because the point of the exercise is
 * the trade-off: a 2% lift is worth knowing about, but not if it takes nine months. It
 * is the only step that does: before it, a length would rest on a lift not yet chosen.
 */
export const PlanWizard = ({ open, onOpenChange, manager }: PlanWizardProps) => {
  const { baseline, assumptions, evaluate, savePlan } = manager;
  const [step, setStep] = useState<Step>('traffic');
  const [liftChoice, setLiftChoice] = useState<string>('5');
  const [customLift, setCustomLift] = useState('');
  const [trafficMode, setTrafficMode] = useState<TrafficMode>('history');
  const [trafficText, setTrafficText] = useState('');
  const [siteShare, setSiteShare] = useState<number>(100);
  const [rateMode, setRateMode] = useState<'history' | 'own'>('history');
  const [rateText, setRateText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const hasTrafficHistory = !!baseline?.dailySessions;
  const hasSiteTraffic = !!baseline?.siteDailySessions;
  const hasRateHistory = !!baseline?.controlRate;

  // Seeded from what is saved each time the dialog opens, as the PXL flow does.
  // Seeded once the figures have arrived, not on open: the wizard fetches them when it
  // opens, and seeding before they land picked "my own figure" for every step.
  useEffect(() => {
    if (!open || manager.isLoading) return;
    const preset = LIFT_PRESETS.find((p) => p.value === assumptions.liftPct);
    setStep('traffic');
    setLiftChoice(preset ? String(preset.value) : 'custom');
    setCustomLift(preset ? '' : String(assumptions.liftPct));
    setTrafficMode(
      assumptions.dailySessions !== null
        ? 'own'
        : assumptions.siteSharePct !== null && hasSiteTraffic
          ? 'site'
          : hasTrafficHistory
            ? 'history'
            : hasSiteTraffic
              ? 'site'
              : 'own',
    );
    setTrafficText(asText(assumptions.dailySessions));
    setSiteShare(assumptions.siteSharePct ?? 100);
    setRateMode(assumptions.ratePct !== null || !hasRateHistory ? 'own' : 'history');
    setRateText(asText(assumptions.ratePct));
  }, [open, manager.isLoading]);

  const liftPct = liftChoice === 'custom' ? parsePositive(customLift) : Number(liftChoice);
  const typedTraffic = trafficMode === 'own' ? parsePositive(trafficText) : null;
  // At least one a day: 0.3 passed as positive and then rounded to nothing on save.
  const ownTraffic = typedTraffic !== null && typedTraffic >= 1 ? typedTraffic : null;
  const ownRate = rateMode === 'own' ? parsePositive(rateText) : null;

  const draft: PlanAssumptions = {
    liftPct: liftPct ?? assumptions.liftPct,
    dailySessions: ownTraffic !== null ? Math.round(ownTraffic) : null,
    siteSharePct: trafficMode === 'site' ? siteShare : null,
    ratePct: ownRate !== null && ownRate < 100 ? ownRate : null,
  };

  const evaluation = evaluate(draft);

  const stepValid: Record<Step, boolean> = {
    lift: liftPct !== null && liftPct <= 500,
    traffic:
      trafficMode === 'history' ? hasTrafficHistory : trafficMode === 'site' ? hasSiteTraffic : ownTraffic !== null,
    rate: rateMode === 'history' ? hasRateHistory : ownRate !== null && ownRate < 100,
    summary: true,
  };

  const index = STEPS.indexOf(step);
  const progress = Math.round(((index + 1) / STEPS.length) * 100);
  const next = () => stepValid[step] && setStep(STEPS[Math.min(index + 1, STEPS.length - 1)]!);
  const back = () => setStep(STEPS[Math.max(index - 1, 0)]!);

  const complete = async () => {
    setIsSaving(true);
    try {
      await savePlan(draft);
      onOpenChange(false);
    } catch {
      // useClient has already shown the error; the dialog stays open so nothing is lost.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-line bg-panel sm:rounded-panel max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-title font-medium">Plan the run length</DialogTitle>
          <DialogDescription className="supporting">
            How long this experiment needs to run to detect the change you care about.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="micro-label">
                Step {Math.min(index + 1, 3)} of 3{step === 'summary' && ' · review'}
              </span>
              <span className="num text-dim">{progress}%</span>
            </div>
            <div className="bg-track h-[3px] w-full overflow-hidden rounded-pill">
              <div className="bg-accent h-full rounded-pill transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {manager.isLoading && <p className="supporting">Loading this website's figures…</p>}

          {step === 'lift' && (
            <StepBody
              title="Lift worth detecting"
              question="What is the smallest improvement you would act on? Each run length uses the traffic and rate you just chose."
            >
              {LIFT_PRESETS.map((preset) => (
                <Option
                  key={preset.value}
                  selected={liftChoice === String(preset.value)}
                  onSelect={() => setLiftChoice(String(preset.value))}
                  label={`+${preset.value}% · ${preset.label}`}
                  hint={preset.hint}
                  aside={formatRunLength(evaluate({ ...draft, liftPct: preset.value }).plan)}
                />
              ))}
              <Option
                selected={liftChoice === 'custom'}
                onSelect={() => setLiftChoice('custom')}
                label="Another size"
                hint="Relative to the original's rate: 5 means +5%."
                aside={liftChoice === 'custom' && liftPct !== null ? formatRunLength(evaluation.plan) : ''}
              >
                {liftChoice === 'custom' && (
                  <NumberField value={customLift} onChange={setCustomLift} placeholder="e.g. 7.5" suffix="%" />
                )}
              </Option>
            </StepBody>
          )}

          {!manager.isLoading && step === 'traffic' && (
            <StepBody
              title="Traffic into the test"
              question="How many sessions a day will reach this experiment's pages?"
            >
              {hasTrafficHistory && (
                <Option
                  selected={trafficMode === 'history'}
                  onSelect={() => setTrafficMode('history')}
                  label={`Typical test here: about ${baseline!.dailySessions!.toLocaleString()} a day`}
                  hint={`What past tests on this website actually received: the median of ${baseline!.experimentsUsed} experiment${baseline!.experimentsUsed === 1 ? '' : 's'}, last ${baseline!.lookbackDays} days.`}
                />
              )}
              {hasSiteTraffic && (
                <Option
                  selected={trafficMode === 'site'}
                  onSelect={() => setTrafficMode('site')}
                  label={`This website's traffic: about ${baseline!.siteDailySessions!.toLocaleString()} a day`}
                  hint="Measured by Pertento over the last 28 days. Pick the share of it this test's pages receive."
                >
                  {trafficMode === 'site' && (
                    <div
                      className="flex flex-wrap items-center gap-1.5"
                      role="radiogroup"
                      aria-label="Share of site traffic"
                    >
                      {SITE_SHARES.map((share) => (
                        <button
                          key={share}
                          type="button"
                          role="radio"
                          aria-checked={siteShare === share}
                          onClick={() => setSiteShare(share)}
                          className={cn(
                            'micro-label rounded-pill border px-2.5 py-1 transition-colors duration-150',
                            siteShare === share
                              ? 'bg-sel-bg border-sel-line text-foreground'
                              : 'border-input text-dim hover:bg-hover hover:text-foreground',
                          )}
                        >
                          {share === 100 ? 'All of it' : `${share}%`}
                        </button>
                      ))}
                      <span className="text-dim text-meta">
                        ≈ {Math.round((baseline!.siteDailySessions! * siteShare) / 100).toLocaleString()} / day
                      </span>
                    </div>
                  )}
                </Option>
              )}
              <Option
                selected={trafficMode === 'own'}
                onSelect={() => setTrafficMode('own')}
                label="My own figure"
                hint={
                  hasTrafficHistory || hasSiteTraffic
                    ? "When you know the traffic to this test's pages better."
                    : 'Pertento has no traffic for this website yet to estimate from.'
                }
              >
                {trafficMode === 'own' && (
                  <NumberField
                    value={trafficText}
                    onChange={setTrafficText}
                    placeholder="e.g. 1500"
                    suffix="/ day"
                    error={trafficText.trim() !== '' && ownTraffic === null ? 'At least one session a day.' : null}
                  />
                )}
              </Option>
            </StepBody>
          )}

          {step === 'rate' && (
            <StepBody
              title="Original's conversion rate"
              question={`How often does the original convert on ${manager.primaryObjective}?`}
            >
              {hasRateHistory && (
                <Option
                  selected={rateMode === 'history'}
                  onSelect={() => setRateMode('history')}
                  label={`${pct(baseline!.controlRate!, 2)}, from this website's history`}
                  hint={`The original's rate on ${manager.primaryObjective} across those experiments.`}
                />
              )}
              <Option
                selected={rateMode === 'own'}
                onSelect={() => setRateMode('own')}
                label="My own figure"
                hint={
                  hasRateHistory
                    ? 'When this page converts differently from the site average.'
                    : `No past conversions on ${manager.primaryObjective} to estimate from.`
                }
              >
                {rateMode === 'own' && (
                  <NumberField
                    value={rateText}
                    onChange={setRateText}
                    placeholder="e.g. 2.5"
                    suffix="%"
                    error={
                      rateText.trim() !== '' && (ownRate === null || ownRate >= 100)
                        ? 'A rate above 0% and below 100%.'
                        : null
                    }
                  />
                )}
              </Option>
            </StepBody>
          )}

          {step === 'summary' && <PlanSummary evaluation={evaluation} liftPct={draft.liftPct} />}

          <div className="flex gap-2">
            {index > 0 && (
              <Button variant="outline" onClick={back} className={cn(SECONDARY_BUTTON_CLASS, 'flex-1')}>
                <ChevronLeft className="size-3.5" strokeWidth={1.5} />
                Back
              </Button>
            )}
            {step === 'summary' ? (
              <Button
                onClick={complete}
                disabled={isSaving || !evaluation.plan}
                className={cn(PRIMARY_BUTTON_CLASS, 'flex-1')}
              >
                {isSaving ? 'Saving…' : 'Save plan'}
              </Button>
            ) : (
              <Button
                onClick={next}
                disabled={manager.isLoading || !stepValid[step]}
                className={cn(PRIMARY_BUTTON_CLASS, index === 0 ? 'w-full' : 'flex-1')}
              >
                Next
                <ChevronRight className="size-3.5" strokeWidth={1.5} />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

type StepBodyProps = { title: string; question: string; children: React.ReactNode };

const StepBody = ({ title, question, children }: StepBodyProps) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col gap-1">
      <h3 className="micro-label text-foreground">{title}</h3>
      <p className="supporting">{question}</p>
    </div>
    <div role="radiogroup" aria-label={title} className="flex flex-col gap-2">
      {children}
    </div>
  </div>
);

type OptionProps = {
  selected: boolean;
  onSelect: () => void;
  label: string;
  hint: string;
  /** The consequence of choosing it, right-aligned: a run length on the lift step. */
  aside?: string;
  children?: React.ReactNode;
};

/** The PXL option row: a card that selects on click, with its detail underneath. */
const Option = ({ selected, onSelect, label, hint, aside, children }: OptionProps) => (
  <div
    role="radio"
    aria-checked={selected}
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        onSelect();
      }
    }}
    className={cn(
      'rounded-card flex cursor-pointer flex-col gap-2 border p-3 transition-colors duration-150',
      selected ? 'border-sel-line bg-sel-bg' : 'border-line bg-panel2 hover:bg-hover',
    )}
  >
    <div className="flex items-start gap-3">
      <span
        className={cn(
          'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border',
          selected ? 'border-accent' : 'border-input',
        )}
      >
        {selected && <span className="bg-accent size-2 rounded-full" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-foreground text-meta">{label}</div>
        <div className="supporting">{hint}</div>
      </div>
      {aside && <span className="num text-dim shrink-0">{aside}</span>}
    </div>
    {children && (
      // A click inside the field must not re-select through the card's handler.
      // A click or key inside the nested field or share buttons must not reach the card:
      // its Enter/Space handler cancelled the nested button's activation, so keyboard
      // users could never pick a share.
      <div className="pl-7" onClick={(ev) => ev.stopPropagation()} onKeyDown={(ev) => ev.stopPropagation()}>
        {children}
      </div>
    )}
  </div>
);

type NumberFieldProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  suffix: string;
  /** Why the typed value cannot be used, or null. Next stays disabled while it is set. */
  error?: string | null;
};

const NumberField = ({ value, onChange, placeholder, suffix, error }: NumberFieldProps) => (
  <div className="flex flex-col gap-1">
    <div className="flex max-w-56 items-center gap-2">
      <Input
        autoFocus
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        value={value}
        placeholder={placeholder}
        aria-invalid={!!error}
        onChange={(ev) => onChange(ev.target.value)}
        className="border-line bg-field text-foreground num h-8 rounded-lg"
      />
      <span className="text-dim shrink-0 text-meta">{suffix}</span>
    </div>
    {error && <p className="text-warn text-micro">{error}</p>}
  </div>
);

type PlanSummaryProps = { evaluation: PlanEvaluation; liftPct: number };

/** The review step: the answers above, and what they add up to. */
const PlanSummary = ({ evaluation, liftPct }: PlanSummaryProps) => {
  const { plan, controlRate, dailySessions } = evaluation;
  if (!plan || controlRate === null || dailySessions === null) {
    return (
      <p className="supporting">
        These answers cannot be planned. Check the lift keeps the rate under 100% and the figures are above zero.
      </p>
    );
  }

  const lift2 = evaluation.liftIn(MIN_PLAN_DAYS);
  const lift4 = evaluation.liftIn(MIN_PLAN_DAYS * 2);

  return (
    <div className="flex flex-col gap-2">
      <SummaryRow label="Run for" value={formatRunLength(plan)} strong />
      <SummaryRow
        label="Sessions per variant"
        value={plan.requiredPerArm.toLocaleString()}
        hint={`To detect +${liftPct}% (${pct(controlRate, 2)} → ${pct(controlRate + plan.absoluteEffect, 2)}) with 80% power.`}
      />
      <SummaryRow label="Traffic into the test" value={`${dailySessions.toLocaleString()} / day`} />
      <SummaryRow
        label="Or, in a shorter run"
        value={`2 wk ${lift2 === null ? 'too little traffic' : `+${pct(lift2)}`} · 4 wk ${lift4 === null ? 'too little traffic' : `+${pct(lift4)}`}`}
        hint="The smallest lift each length could reliably detect."
      />
      {plan.comparisons > 1 && (
        <p className="supporting">
          Sized for {plan.comparisons} comparisons against the original, each at {pct(plan.alpha, 2)} significance.
        </p>
      )}
    </div>
  );
};

type SummaryRowProps = { label: string; value: string; hint?: string; strong?: boolean };

const SummaryRow = ({ label, value, hint, strong }: SummaryRowProps) => (
  <div className="border-line bg-panel2 rounded-card flex items-center justify-between gap-3 border p-3">
    <div className="min-w-0">
      <div className="text-foreground text-body font-medium">{label}</div>
      {hint && <div className="supporting">{hint}</div>}
    </div>
    <div className={cn('shrink-0 text-right', strong ? 'metric-value text-foreground' : 'num text-foreground')}>
      {value}
    </div>
  </div>
);
